import React, { useContext, useEffect, useRef, useState } from 'react';
import { createSearchParams, useNavigate, useParams } from 'react-router-dom';
import {
	ChevronRight,
	ContentWrapper,
	IconAfter,
	IconButton,
	List,
	Typography,
	Wrapper,
	withLocalization,
	NoCompletedTrips,
	Toast,
	ErrorCrossIcon,
	renderFlexServiceOrderSummary,
	ContentLoader,
} from '@adac-mp/components';
import {
	IListItemProps,
	IOrderInfoDetails,
	IOrderInfoResponse,
	ITypeVignettesOrderManagementFields,
	Locales,
	OfferContext,
	OrderService,
	VignetteContext,
	formatAndSortPassages,
	getErrorTextFromResponse,
	getLabelValue,
	getResponseErrorNumber,
	getTripsOerviewCollection,
	getUrlBasePath,
	setRouteLangParam,
} from '@adac-mp/shared';
import { useQuery } from '@apollo/client';

const TripsOverviewPage = () => {
	const navigate = useNavigate();
	const { lang } = useParams();
	const { labels } = useContext(OfferContext);
	const { orderData, updateNewOrderData, updateContentfulLabels, contentfulLabels } = useContext(VignetteContext);
	const [tripsOverviewList, setTripsOverviewList] = useState<IListItemProps[] | []>([]);
	const [flexService, setFlexService] = useState<IOrderInfoDetails | undefined>(undefined);
	const [sortDescending, setSortDescending] = useState<boolean>(false);
	const [apiError, setApiError] = useState<string | undefined>(undefined);
	const unmountComponent = useRef(false);
	const { error, data, loading } = useQuery(getTripsOerviewCollection, {
		variables: { lang: lang || 'de' },
	});

	useEffect(() => {
		if (!error) {
			updateContentfulLabels(data?.vignettesOrderManagementCollection.items[0]);
		}
	}, [data]);

	const currentLabels = contentfulLabels as ITypeVignettesOrderManagementFields;

	const handleOrderException = (res: IOrderInfoResponse) => {
		const errorNumber = getResponseErrorNumber(res.errors);
		if (errorNumber === 3300) {
			navigate(getUrlBasePath(`/link-expired${setRouteLangParam(lang)}`), { replace: true });
		}
		const errorText = getErrorTextFromResponse(res.errors, getLabelValue('mp.generalError', labels));
		setApiError(errorText);
	};

	useEffect(() => {
		const orderHash = sessionStorage.getItem('orderHash');
		const orderDetailUuid = sessionStorage.getItem('orderDetailUuid');
		const token = sessionStorage.getItem('token');

		if (!tripsOverviewList.length) {
			if (orderHash && orderDetailUuid && token) {
				OrderService.getPassage({ orderDetailUuid }, token, lang as Locales).then((res) => {
					if (res.code === 200 && res.passages.length > 0) {
						const currPassages = formatAndSortPassages(res.passages);
						setTripsOverviewList(currPassages);
					}
				});
			} else {
				const params = createSearchParams();
				params.set('orderHash', String(orderHash));
				navigate(getUrlBasePath(`order-management${setRouteLangParam(lang)}`, params), {
					replace: true,
				});
			}
		}

		if (!orderData && orderHash) {
			OrderService.getOrderInfo({ orderHash }, lang as Locales).then((res) => {
				if (res.code === 200) {
					updateNewOrderData(res);
					const currFlexService = res.orderInfo.orderDetails.find(
						(order) => order.orderDetailUuid === orderDetailUuid,
					);
					setFlexService(currFlexService);
					sessionStorage.setItem('orderHash', res.orderInfo.orderHash);
					sessionStorage.setItem('customerHash', res.orderInfo.customerHash);
					sessionStorage.setItem('token', res.token);
				} else {
					handleOrderException(res);
				}
			});
		} else {
			const currFlexService = orderData?.orderInfo.orderDetails.find(
				(order) => order.orderDetailUuid === orderDetailUuid,
			);
			setFlexService(currFlexService);
		}
	}, [lang]);

	useEffect(() => {
		return () => {
			if (unmountComponent.current) {
				sessionStorage.removeItem('orderDetailUuid');
			} else {
				unmountComponent.current = true;
			}
		};
	}, []);

	const renderTripsOverview = () =>
		tripsOverviewList.length === 0 ? (
			<div className="flex flex-col mx-auto items-center self-center gap-2 md:w-1/2 pt-3">
				<NoCompletedTrips />
				{/* Contentful */}
				<Typography className="text-center" size="lgEven" color="title">
					{currentLabels?.noTripsFound || 'Es wurden keine Fahrten unter diesem Kennzeichen gefunden.'}
				</Typography>
			</div>
		) : (
			<>
				<IconButton
					className="border-none w-fit inline-flex items-center self-end gap-2 text-gray-200 font-bold text-xl transition-colors group hover:text-gray-100 mb-3"
					onClick={() => {
						setTripsOverviewList((prev) => [...prev].reverse());
						setSortDescending((s) => !s);
					}}>
					{`${sortDescending ? currentLabels?.newest || 'Neueste' : currentLabels?.oldest || 'Älteste'} ${
						currentLabels?.orderedTrips || 'Fahrten zuerst'
					}`}
					<IconAfter className="group-hover:[&>*]:fill-gray-100" />
				</IconButton>
				<List
					fullSpace
					labelVariant="title"
					valueVariant="title"
					valueColor="lightText"
					items={tripsOverviewList}
				/>
			</>
		);

	return (
		<>
			{loading ? (
				<ContentLoader isLoading hasPortal={false} />
			) : (
				<Wrapper>
					<IconButton
						className="border-none w-fit inline-flex items-center gap-2 text-gray-200 font-bold text-xl transition-colors group hover:text-gray-100 mb-4 md:mb-3"
						onClick={() => {
							const orderHash = sessionStorage.getItem('orderHash');
							const params = createSearchParams();
							params.set('orderHash', String(orderHash));
							navigate(getUrlBasePath(`/order-management${setRouteLangParam(lang)}`, params));
						}}>
						<ChevronRight fill="#3D3D3D" className="rotate-180 group-hover:[&>*]:fill-gray-100" />
						{currentLabels?.backToOverview || 'Zurück zur Bestellübersicht'}
					</IconButton>
					<Typography
						className="flex self-center mb-4 md:mb-5"
						component="h1"
						size="h2"
						variant="header"
						color="title">
						{currentLabels?.tripsOverview || 'Fahrtenübersicht'}
					</Typography>
					<ContentWrapper>
						{flexService && renderFlexServiceOrderSummary(flexService, currentLabels, labels)}
						{renderTripsOverview()}
					</ContentWrapper>
					{apiError && (
						<Toast
							content={apiError}
							icon={<ErrorCrossIcon />}
							onToastFinished={() => setApiError(undefined)}
						/>
					)}
				</Wrapper>
			)}{' '}
		</>
	);
};

export const TripsOverview = withLocalization(TripsOverviewPage);
